import React, { useState } from "react";
import { IKImage } from "imagekitio-next";
import { useTranslation } from "next-i18next";

import Carousel from "components/gallery/Carousel";
import AvatarIcon from "components/customImages/AvatarIcon";
import { getImageName } from "helpers";
import goldenStar from "/public/images/golden-star.svg";
import grayStar from "/public/images/gray-star.svg";
import Image from "next/image";
import AvatarIconImage from "components/customImages/AvatarIconImage";
import { COMMENT_TYPE, FormattedCommentType } from "helpers/types";
import AdminCommentImage from "components/customImages/adminCommentImage";

type CommentPropsType = {
  comment: FormattedCommentType;
  commentType: COMMENT_TYPE;
};

const COMMENT_IMAGE_SIZE = 150;
function Comment({ comment, commentType }: CommentPropsType) {
  const { t: categoriesTranslate } = useTranslation("categories");
  const { t: commonTranslate } = useTranslation("common");
  const [carouselIsOpen, setCarouselIsOpen] = useState<boolean>(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
  const [hasAdminCommentImage, setHasAdminCommentImage] = useState<boolean>(
    !!comment?.commentImage?.url,
  );
  //Carousel settings
  function closeCarousel() {
    setCarouselIsOpen(false);
  }

  function openCarousel(imageIndex: number) {
    setSelectedImageIndex(imageIndex);
    setCarouselIsOpen(true);
  }

  if (!comment) {
    return "";
  }

  return (
    <div className="bg-backgroundGrey/40 p-4 lg:p-6 mb-6 rounded-lg  grid gap-2">
      <div className="flex items-center gap-16  ">
        <div className="flex items-center gap-3">
          <div className="self-start">
            {commentType === COMMENT_TYPE.YAWAKIT_COMMENT ? (
              <AvatarIcon
                imageName={comment.userProfilePicture}
                alt={commonTranslate("profile_Logo")}
              />
            ) : (
              <AvatarIconImage
                googleComment={commentType === COMMENT_TYPE.GOOGLE_COMMENT}
                facebookComment={commentType === COMMENT_TYPE.FACEBOOK_COMMENT}
                alt={commonTranslate("profile_Logo")}
                imageUrl={comment.userProfilePicture}
              />
            )}
          </div>
          <div className="grid gap-1   items-center">
            <div className="flex justify-between">
              <p className="font-normal text-sm  lg:text-xl leading-4">{comment.fullName}</p>
              {comment.rating && comment.rating > 1 && (
                <div className="flex gap-1">
                  {Array.from({ length: Math.ceil(comment.rating) }).map((_, index: number) => {
                    return (
                      <Image
                        key={`star__${index}`}
                        src={goldenStar}
                        alt={"gold star"}
                        width={15}
                        height={15}
                      />
                    );
                  })}
                  {Array.from({ length: 5 - Math.ceil(comment.rating) }).map((_, index: number) => {
                    return (
                      <Image
                        key={`star__${index}`}
                        src={grayStar}
                        alt={"gray star"}
                        width={15}
                        height={15}
                      />
                    );
                  })}
                </div>
              )}
            </div>
            <p className="text-left rtl:text-right text-xs lg:text-sm text-SilverGray">
              {comment.date}
            </p>
          </div>
        </div>

        {hasAdminCommentImage && (
          <div className="hidden lg:flex flex-col gap-4 ">
            {comment.comment && (
              <p className="font-medium text-xs lg:text-sm ">{comment.comment}</p>
            )}
            <div
              className="h-full "
              onClick={() => {
                openCarousel(0);
              }}
            >
              <AdminCommentImage
                imageUrl={String(comment.commentImage?.url)}
                width={Number(comment.commentImage?.width)}
                height={Number(comment.commentImage?.height)}
              />
            </div>
          </div>
        )}
      </div>
      <div className={`flex flex-col gap-4 ${hasAdminCommentImage ? "" : "lg:pl-16"}`}>
        {hasAdminCommentImage ? (
          <React.Fragment>
            {comment.comment && (
              <p className="font-medium text-xs lg:text-sm lg:hidden">{comment.comment}</p>
            )}
            <div
              className="h-full lg:hidden"
              onClick={() => {
                openCarousel(0);
              }}
            >
              <AdminCommentImage
                imageUrl={String(comment.commentImage?.url)}
                width={Number(comment.commentImage?.width)}
                height={Number(comment.commentImage?.height)}
              />
            </div>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <p className="font-medium text-xs lg:text-sm">{comment.comment}</p>

            <div className="grid grid-cols-3 md:flex  gap-3">
              {comment.images?.map((image: string, index: number) => (
                <IKImage
                  key={image}
                  onClick={() => {
                    openCarousel(index);
                  }}
                  urlEndpoint={process.env.NEXT_PUBLIC_BASE_IMAGE_KIT_URL}
                  {...(image?.startsWith("https://")
                    ? { src: image }
                    : { path: getImageName(image) || "default-image.jpg" })}
                  transformation={[{ crop: "maintain_ratio" }]}
                  width={COMMENT_IMAGE_SIZE}
                  height={COMMENT_IMAGE_SIZE}
                  alt={categoriesTranslate(`imageNotFound`)}
                  lqip={{ active: true, quality: 20 }}
                  loading="lazy"
                  className="rounded-md cursor-pointer hover:scale-105 transition-transform duration-200 ease-in-out"
                />
              ))}
            </div>
          </React.Fragment>
        )}

        {carouselIsOpen && (
          <Carousel
            images={hasAdminCommentImage ? [comment.commentImage?.url] : comment.images}
            closeCarousel={closeCarousel}
            enableSwipe={window.innerWidth <= 1024}
            showNavigationButton={window.innerWidth > 1024}
            selectedImageIndex={selectedImageIndex}
          />
        )}
      </div>
    </div>
  );
}

export default Comment;
